/**
 * LatexMessage Component
 *
 * Renders chat message text as Markdown with embedded LaTeX math.
 * - Markdown: bold, italics, lists, etc. (via react-markdown + remark-gfm)
 * - Math: $...$ inline and $$...$$ block (via remark-math + rehype-katex)
 *
 * Replaces the earlier hand-rolled $-splitter, which rendered surrounding text
 * as plain text (so **bold** leaked) and mishandled multi-line/display math.
 *
 * Props:
 * - text: The message text (Markdown + LaTeX)
 * - className: Additional CSS classes
 */
import React from "react";
import "katex/dist/katex.min.css";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";

// Some models emit \( \) / \[ \] delimiters instead of $ / $$. Convert them as
// MATCHED PAIRS so remark-math picks the math up, and so an unbalanced delimiter
// can't cascade into every following span.
//
// NB: a per-delimiter `.replace(/\\\[/g, "$$")` is WRONG — in a replacement
// string "$$" is the escape for a single literal "$", so \[ would become "$"
// (inline), silently downgrading display math. Using function replacers here
// sidesteps that: the returned string is inserted verbatim.
function normalizeDelimiters(text) {
  return (text || "")
    // Display \[ ... \] -> $$ ... $$
    .replace(/\\\[([\s\S]*?)\\\]/g, (_, inner) => "$$" + inner + "$$")
    // Inline \( ... \) -> $ ... $
    .replace(/\\\(([\s\S]*?)\\\)/g, (_, inner) => "$" + inner + "$");
}

// Reunite fragmented math. Some models (seen with the ideal-gas constant) split
// ONE formula into a $$value$$ block plus separate inline unit spans, which
// renders as a centered number with units scattered across the line. Opt-in via
// the `coalesceMath` prop and applied ONLY to AI-generated messages — the human
// teaching chat and quizzes never call this, so their rendering is unchanged.
//
// Two narrow passes, both no-ops on well-formed formulas:
//   1. Demote single-line $$...$$ to inline $...$ (kills the mid-sentence line
//      break). True multi-line display blocks (inner spans a newline) are kept.
//   2. Merge adjacent inline spans separated only by whitespace / · / ⋅ / , into
//      one span, so scattered units rejoin.
function coalesceMathSpans(text) {
  if (!text) return text;
  let t = text.replace(/\$\$([^\n]+?)\$\$/g, (_, inner) => "$" + inner.trim() + "$");
  const adjacent = /\$([^$\n]+?)\$(\s*[·⋅,]?\s*)\$([^$\n]+?)\$/;
  let prev;
  do {
    prev = t;
    t = t.replace(adjacent, (_, a, sep, b) => {
      const joiner = /[·⋅]/.test(sep) ? "\\cdot " : " ";
      return "$" + a.trim() + joiner + b.trim() + "$";
    });
  } while (t !== prev);
  return t;
}

const REMARK_PLUGINS = [remarkGfm, remarkMath];
// throwOnError:false → a malformed formula renders in red rather than blanking
// the whole message.
const REHYPE_PLUGINS = [[rehypeKatex, { throwOnError: false, errorColor: "#cc0000" }]];

export function LatexMessage({ text, className = "", coalesceMath = false }) {
  if (!text) return null;
  const normalized = normalizeDelimiters(text);
  const prepared = coalesceMath ? coalesceMathSpans(normalized) : normalized;
  return (
    <div className={`latex-message ${className}`}>
      <ReactMarkdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS}>
        {prepared}
      </ReactMarkdown>
    </div>
  );
}
